import { ColumnComponent, GroupComponent, Options, RowComponent } from 'tabulator-tables';
import GridConfigAdapterService from './adapter/grid-config-adapter.service';
import { ActionRegistry, GridConfigDto, TitleRegistry } from './adapter/grid-config-adapter.types';
import { MassiaDataShareService, MassiaTabulatorRemoteConfigBuilder } from './builders/massia.tabulator.config.builder';
import { MassiaTabulatorManager } from './core/massia.tabulator.manager';
import { MassiaTabulatorFilterPopupHelpers } from './helpers/filter.popup.helpers';
import { MassiaActionDispatcher } from './massia.action.dispatcher';
import { MassiaAfterLoadCallback, MassiaFetchCallback, MassiaTabulatorColumnDef, MassiaTabulatorGridConfig } from './massia.tabulator.type';
import { StateService } from '@uirouter/angularjs';

/** Colonne visible exposée dans le menu global. */
interface MassiaColumnVisibility {
	label: string;
	field: string;
	visible: boolean;
	component: ColumnComponent;
}

export default class MassiaTabulatorController {
	// AngularJS bindings
	public dataset: unknown[] = [];
	public gridConfig!: MassiaTabulatorGridConfig;
	public gridApi: unknown;
	public options?: Partial<Options>;
	public onCellEdited?: (event: { $event: { row: unknown; field: string; value: unknown } }) => void;

	// Elements interne UI
	public selectedRows: RowComponent[] = [];
	public selectedData: unknown[] = [];
	public searchQuery = '';
	public compactView = false;
	public menuOpen = false;
	public exportMenuOpen = false;
	public allColumns: MassiaColumnVisibility[] = [];

	private domReady = false;
	private pendingInit = false;
	private manager: MassiaTabulatorManager;
	private _activeMenuCloseListener: ((e: Event) => void) | null = null;

	private readonly $element: ng.IAugmentedJQuery;
	private readonly $scope: ng.IScope;
	private readonly $timeout: ng.ITimeoutService;
	private readonly notification: { success: (m: string) => void; error: (m: string) => void };
	private readonly $compile: ng.ICompileService;
	private readonly GridConfigAdapterService: GridConfigAdapterService;
	private readonly MassiaActionDispatcher: MassiaActionDispatcher;
	private readonly DataShareService: MassiaDataShareService;
	private readonly $state: StateService;

	constructor(
		$element: ng.IAugmentedJQuery,
		$scope: ng.IScope,
		$timeout: ng.ITimeoutService,
		notification: { success: (m: string) => void; error: (m: string) => void },
		$compile: ng.ICompileService,
		GridConfigAdapterService: GridConfigAdapterService,
		MassiaActionDispatcher: MassiaActionDispatcher,
		DataShareService: MassiaDataShareService,
		$state: StateService
	) {
		this.$element = $element;
		this.$scope = $scope;
		this.$state = $state;
		this.$timeout = $timeout;
		this.notification = notification;
		this.$compile = $compile;
		this.GridConfigAdapterService = GridConfigAdapterService;
		this.MassiaActionDispatcher = MassiaActionDispatcher;
		this.DataShareService = DataShareService;

		// Initialisation du Manager (Délégation technique)
		this.manager = new MassiaTabulatorManager(
			this.$scope,
			this.notification,
			this.onRowSelectionChanged.bind(this),
			this.onCellEditedCb.bind(this)
		);
	}

	private onRowSelectionChanged(data: unknown[], rows: RowComponent[]): void {
		this.selectedData = data;
		this.selectedRows = rows;
	}

	private onCellEditedCb(event: { $event: { row: unknown; field: string; value: unknown } }): void {
		if (this.onCellEdited) this.onCellEdited(event);
	}

	// ─── AngularJS lifecycle ──────────────────────────────────────────────────

	$onInit(): void {
		// Expose les méthodes publiques du contrôleur Tabulator vers le composant parent
		// via le binding 'gridApi', permettant de piloter la grille de l'extérieur (ex: forcer le rechargement).
		if (this.gridApi && typeof this.gridApi === 'object') {
			const api = this.gridApi as Record<string, unknown>;
			api.initServerGridConfig = this.initServerGridConfig.bind(this);
			api.setData = this.setData.bind(this);
			api.getColumns = () => this.manager.getColumns();
		}
	}

	$postLink(): void {
		// Le $timeout permet d'attendre la fin du cycle digest d'AngularJS (rendu complet du DOM).
		// C'est indispensable pour Tabulator, qui a besoin que l'élément HTML conteneur
		// soit vraiment dessiné à l'écran avec ses dimensions réelles avant de s'initialiser.
		this.$timeout(() => {
			this.domReady = true;
			if (this.pendingInit) {
				this.pendingInit = false;
				this.initTable();
			}
		}, 0);
	}

	//methode utilisé par les controllers metiers pour initialiser la config
	async initServerGridConfig(options: {
		configFn: () => Promise<GridConfigDto>;
		ajaxURL: string;
		dataShareKey: string;
		fetchFn: MassiaFetchCallback;
		onAfterLoad?: MassiaAfterLoadCallback;
		onParams?: (params: Record<string, unknown>) => void;
		customActions?: ActionRegistry;
		titleRegistry?: TitleRegistry;
		onAfterHttpAction?: () => void;
		getCurrentContext?: () => unknown;
	}) {
		const rawServerConfig = await options.configFn();

		const genericRegistry = this.MassiaActionDispatcher.buildRegistryFromConfig(
			rawServerConfig,
			options.onAfterHttpAction ?? (() => this.setData()),
			() => {
				// Avant de retourner le contexte au dispatcher (ex: pour navigation), on sauve l'état de la grille
				this.saveGridState();
				return options.getCurrentContext ? options.getCurrentContext() : {};
			},
			options.dataShareKey
		);

		const actionRegistry: ActionRegistry = {
			...genericRegistry,
			...(options.customActions || {})
		};

		const adaptedConfig = this.GridConfigAdapterService.adapt(
			rawServerConfig,
			actionRegistry,
			options.titleRegistry,
			this.$scope,
			this.$compile,
			this.$state
		);

		this.gridConfig = MassiaTabulatorRemoteConfigBuilder.build({
			serverConfig: { ...rawServerConfig, ...adaptedConfig } as GridConfigDto,
			ajaxURL: options.ajaxURL,
			dataShareKey: options.dataShareKey,
			DataShareService: this.DataShareService,
			fetchFn: options.fetchFn,
			onAfterLoad: options.onAfterLoad,
			onParams: options.onParams
		}) as unknown as MassiaTabulatorGridConfig;

		if (!this.manager.tabulatorInstance) {
			this.initTable();
		} else {
			this.manager.setColumns(this.gridConfig.columns || []);
		}

		return this.gridConfig;
	}

	private initTable(): void {
		const isRemote = this.gridConfig?.features?.remote?.enabled;
		if (!this.domReady || !this.gridConfig || (!isRemote && !this.dataset)) {
			this.pendingInit = true;
			return;
		}
		const tableElement = this.$element[0].querySelector('.tabulator-container');
		if (!tableElement) {
			console.error('[MassiaTabulator] .tabulator-container element not found!');
			return;
		}

		// Délégation au Manager
		this.manager.init(tableElement as HTMLElement, this.gridConfig, this.dataset, this.options, this.searchQuery);
	}

	public getColumns(): ColumnComponent[] {
		return this.manager.getColumns();
	}

	public setColumns(columns: MassiaTabulatorColumnDef[]): void {
		this.manager.setColumns(columns);
	}

	public setData(): void {
		const isRemote = this.gridConfig?.features?.remote?.enabled || false;
		const remoteAjaxUrl = this.gridConfig?.features?.remote?.ajaxURL as string | undefined;
		this.manager.setData(isRemote, remoteAjaxUrl);
	}

	$onDestroy(): void {
		if (this._activeMenuCloseListener) {
			document.removeEventListener('click', this._activeMenuCloseListener);
			this._activeMenuCloseListener = null;
		}
		// Sauvegarde l'état une dernière fois avant la destruction pour les navigations hors-grid
		this.saveGridState();
		this.manager.destroy();
		MassiaTabulatorFilterPopupHelpers.destroyAllFilterPopups();
	}

	$onChanges(changes: Record<string, { currentValue: unknown }>): void {
		if (changes['gridConfig']?.currentValue) {
			if (!this.manager.tabulatorInstance) {
				this.initTable();
			} else {
				const newConfig = changes['gridConfig'].currentValue as MassiaTabulatorGridConfig;
				if (newConfig?.columns) {
					this.manager.setColumns(newConfig.columns);
				}
			}
		}

		if (changes['dataset']?.currentValue !== undefined) {
			this.dataset = changes['dataset'].currentValue as unknown[];
			const isRemote = this.gridConfig?.features?.remote?.enabled;
			if (isRemote) return;

			if (!this.manager.tabulatorInstance) {
				this.initTable();
				return;
			}
			// Délégation du raffraichissement local
			this.manager.setLocalData(this.dataset as Record<string, unknown>[]);
		}
	}

	/** Sauvegarde l'état courant de la grille dans le DataShare */
	private saveGridState(): void {
		const onSaveGridState = this.gridConfig?.features?.persistence?.onSaveGridState;
		if (!onSaveGridState || !this.manager.tabulatorInstance) return;

		onSaveGridState(() => ({
			page: this.manager.tabulatorInstance!.getPage() as number,
			size: this.manager.tabulatorInstance!.getPageSize() as number,
			sort: this.manager.tabulatorInstance!.getSorters() as unknown[],
			filter: this.manager.tabulatorInstance!.getHeaderFilters() as { field: string; value: unknown }[]
		}));
	}

	// ─── Public UI methods ───────────────────────────────────────────────────

	toggleCompactView(): void {
		this.compactView = !this.compactView;
		if (this.manager.tabulatorInstance) {
			this.manager.tabulatorInstance.element.classList.toggle('tabulator-compact', this.compactView);
			this.manager.tabulatorInstance.redraw();
		}
	}

	toggleGlobalMenu(event: Event): void {
		event.stopPropagation();
		this.menuOpen = !this.menuOpen;
		this.exportMenuOpen = false;

		if (this.menuOpen && this.manager.tabulatorInstance) {
			this.allColumns = this.manager.tabulatorInstance
				.getColumns()
				.filter((col: ColumnComponent) => col.getDefinition().formatter !== 'rowSelection')
				.map((col: ColumnComponent) => ({
					label: col.getDefinition().title,
					field: col.getField(),
					visible: col.isVisible(),
					component: col
				}));

			const closeMenu = (e: Event) => {
				if ((e.target as HTMLElement).closest('.tabulator-global-menu')) return;
				this.$scope.$apply(() => (this.menuOpen = false));
				document.removeEventListener('click', closeMenu);
				if (this._activeMenuCloseListener === closeMenu) this._activeMenuCloseListener = null;
			};
			this._activeMenuCloseListener = closeMenu;
			setTimeout(() => document.addEventListener('click', closeMenu), 0);
		}
	}

	toggleExportMenu(event: Event): void {
		event.stopPropagation();
		this.exportMenuOpen = !this.exportMenuOpen;
		this.menuOpen = false;

		if (this.exportMenuOpen) {
			const closeMenu = (e: Event) => {
				if ((e.target as HTMLElement).closest('.tabulator-export-menu')) return;
				this.$scope.$apply(() => (this.exportMenuOpen = false));
				document.removeEventListener('click', closeMenu);
				if (this._activeMenuCloseListener === closeMenu) this._activeMenuCloseListener = null;
			};
			this._activeMenuCloseListener = closeMenu;
			setTimeout(() => document.addEventListener('click', closeMenu), 0);
		}
	}

	ungroupAll(): void {
		this.menuOpen = false;
		this.manager.tabulatorInstance?.setGroupBy('');
	}

	toggleColumnFromRow(col: MassiaColumnVisibility, event: Event): void {
		console.log('🚀 ~ MassiaTabulatorController ~ toggleColumnFromRow ~ event:', event);
		// Le clic sur le switch est déjà géré par stopPropagation dans le template.
		// On ne rentre ici que si on clique sur le texte/label de la ligne.
		col.visible = !col.visible;
		this.onColumnVisibilityChange(col);
	}

	onColumnVisibilityChange(col: MassiaColumnVisibility): void {
		// On laisse le cycle digest se terminer pour être sûr de l'état du modèle
		this.$timeout(() => {
			if (col.visible) {
				col.component.show();
			} else {
				col.component.hide();
			}
		}, 0);
	}

	expandAll(): void {
		(this.manager.tabulatorInstance?.getGroups() || []).forEach((g: GroupComponent) => g.show());
	}

	collapseAll(): void {
		(this.manager.tabulatorInstance?.getGroups() || []).forEach((g: GroupComponent) => g.hide());
	}

	isGrouped(): boolean {
		return this.getCurrentGroupByFields().length > 0;
	}

	onSearch(): void {
		this.manager.tabulatorInstance?.redraw(true);
	}

	clearFilters(): void {
		if (this.manager.tabulatorInstance) {
			this.manager.tabulatorInstance.clearHeaderFilter();
			this.manager.tabulatorInstance.clearFilter(true);
			this.searchQuery = '';
			this.manager.tabulatorInstance.redraw(true);
		}
	}

	copyToClipboard(): void {
		if (this.manager.tabulatorInstance) {
			this.manager.tabulatorInstance.copyToClipboard('active');
			this.notification.success('Données copiées dans le presse-papier !');
		}
	}

	exportData(type: 'json' | 'csv'): void {
		if (this.manager.tabulatorInstance) {
			if (type === 'json') this.manager.tabulatorInstance.download('json', 'data.json');
			else if (type === 'csv') this.manager.tabulatorInstance.download('csv', 'data.csv');
		}
	}

	// ─── Grouping helpers ─────────────────────────────────────────────────────

	private getCurrentGroupByFields(): string[] {
		if (!this.manager.tabulatorInstance || !this.manager.tableReady) return [];
		const groups = this.manager.tabulatorInstance.getGroups();
		const fields: string[] = [];
		if (groups?.length) {
			let current = groups[0];
			while (current && (current as unknown as { getField: () => string }).getField) {
				fields.push((current as unknown as { getField: () => string }).getField());
				const subs = (current as unknown as { getSubGroups: () => unknown[] }).getSubGroups();
				current = subs?.length ? (subs[0] as typeof current) : (null as unknown as typeof current);
			}
		}
		return fields;
	}

	private groupByColumn(field: string): void {
		if (this.manager.tabulatorInstance) {
			const current = this.getCurrentGroupByFields();
			this.manager.tabulatorInstance.setGroupBy([...current, field]);
		}
	}
}

MassiaTabulatorController.$inject = [
	'$element',
	'$scope',
	'$timeout',
	'notification',
	'$compile',
	'GridConfigAdapterService',
	'MassiaActionDispatcher',
	'DataShareService',
	'$state'
];
